import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import logoMirai from '../assets/mirai_innovation_logo.jpeg';
import { PLATFORM_PDF_FOOTER } from '../constants/platform';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 30,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    borderBottom: '2 solid #dc3545',
    paddingBottom: 15,
  },
  logo: {
    width: 60,
    height: 60,
    marginRight: 15,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#dc3545',
    flex: 1,
  },
  subtitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginTop: 20,
    marginBottom: 10,
    borderBottom: '1 solid #e9ecef',
    paddingBottom: 5,
  },
  card: {
    backgroundColor: '#f8f9fa',
    padding: 15,
    marginBottom: 15,
    borderRadius: 8,
    border: '1 solid #dee2e6',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  col: {
    flex: 1,
    marginRight: 10,
  },
  statCard: {
    backgroundColor: '#ffffff',
    padding: 12,
    marginBottom: 10,
    borderRadius: 6,
    border: '1 solid #e9ecef',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  statTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 8,
  },
  riskLevel: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 5,
    padding: 5,
    borderRadius: 4,
  },
  riskLabel: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  riskPercentage: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  userCard: {
    backgroundColor: '#fff5f5',
    padding: 10,
    marginBottom: 8,
    borderRadius: 6,
    border: '1 solid #fecaca',
  },
  userName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#dc3545',
    marginBottom: 4,
  },
  userInfo: {
    fontSize: 10,
    color: '#6c757d',
    marginBottom: 2,
  },
  riskIndicator: {
    fontSize: 10,
    color: '#dc3545',
    fontWeight: 'bold',
    marginTop: 4,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 10,
    color: '#6c757d',
    borderTop: '1 solid #e9ecef',
    paddingTop: 10,
  },
  watermark: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: 120,
    color: '#f0f0f0',
    opacity: 0.1,
    zIndex: -1,
  },
  summaryGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  summaryCard: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    padding: 15,
    margin: 5,
    borderRadius: 8,
    border: '1 solid #dee2e6',
  },
  summaryTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 10,
    textAlign: 'center',
  },
  summaryNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#dc3545',
    textAlign: 'center',
  },
  summaryLabel: {
    fontSize: 12,
    color: '#6c757d',
    textAlign: 'center',
    marginTop: 5,
  },
  chartContainer: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    border: '1 solid #dee2e6',
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 10,
    textAlign: 'center',
  },
  chartImage: {
    width: '100%',
    height: 200,
    marginBottom: 10,
  },
  chartDescription: {
    fontSize: 10,
    color: '#6c757d',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  chartGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  chartItem: {
    width: '48%',
    marginBottom: 15,
  },
});

const AdminReportPDF = ({ 
  users, 
  riskStats, 
  totalUsers, 
  completedAssessments,
  surveySummary,
  chartImages = {} 
}) => {
  const currentDate = new Date().toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Función para generar gráfica de barras simple con texto
  const generateTextChart = (data, title, maxValue = 100) => {
    if (!data || Object.keys(data).length === 0) {
      console.log(`No data available for chart: ${title}`);
      return null;
    }
    
    const entries = Object.entries(data);
    if (entries.length === 0) {
      console.log(`Empty entries for chart: ${title}`);
      return null;
    }
    
    const maxCount = Math.max(...entries.map(([_, count]) => Number(count) || 0));
    console.log(`Generating chart: ${title}, entries:`, entries, 'maxCount:', maxCount);
    
    return (
      <View style={styles.chartContainer}>
        <Text style={styles.chartTitle}>{title}</Text>
        {entries.map(([label, count]) => {
          const numCount = Number(count) || 0;
          const percentage = maxCount > 0 ? (numCount / maxCount) * 100 : 0;
          const barLength = Math.round(percentage / 5); // Escala de 0-20 caracteres
          const bar = '█'.repeat(Math.max(0, barLength)) + '░'.repeat(Math.max(0, 20 - barLength));
          
          return (
            <View key={label} style={{ marginBottom: 5 }}>
              <Text style={{ fontSize: 10, color: '#2c3e50' }}>
                {label}: {numCount} ({Math.round(percentage)}%)
              </Text>
              <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier' }}>
                {bar}
              </Text>
            </View>
          );
        })}
      </View>
    );
  };

  // Función para mostrar gráfica como imagen si está disponible
  const renderChartImage = (chartId, title, fallbackData) => {
    const imageData = chartImages[chartId];
    
    if (imageData) {
      return (
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>{title}</Text>
          <Image src={imageData} style={styles.chartImage} />
          <Text style={styles.chartDescription}>Gráfica generada automáticamente</Text>
        </View>
      );
    }
    
    // Fallback a gráfica de texto si no hay imagen
    return fallbackData ? generateTextChart(fallbackData, title) : null;
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'Bajo':
      case 'Riesgo Bajo':
        return '#28a745';
      case 'Moderado':
      case 'Riesgo Moderado':
        return '#ffc107';
      case 'Alto':
      case 'Riesgo Alto':
        return '#fd7e14';
      case 'Grave':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Marca de agua */}
        <Text style={styles.watermark}>MIRAI</Text>

        {/* Header */}
        <View style={styles.header}>
          <Image src={logoMirai} style={styles.logo} />
          <Text style={styles.title}>Reporte de Administración</Text>
        </View>

        {/* Información general */}
        <View style={styles.card}>
          <Text style={styles.subtitle}>Información General</Text>
          <View style={styles.row}>
            <Text>Fecha del reporte: {currentDate}</Text>
          </View>
          <View style={styles.row}>
            <Text>Total de usuarios registrados: {totalUsers}</Text>
          </View>
          <View style={styles.row}>
            <Text>Evaluaciones completadas: {completedAssessments}</Text>
          </View>
          <View style={styles.row}>
            <Text>Usuarios de alto riesgo: {riskStats?.totalHighRiskUsers || 0}</Text>
          </View>
        </View>

        {/* Estadísticas de Encuestas */}
        {surveySummary && (
          <View style={styles.card}>
            <Text style={styles.subtitle}>Estadísticas de Encuestas</Text>
            <View style={styles.row}>
              <Text>Demográficos: {surveySummary.general?.completionRates?.demographic || 0}%</Text>
            </View>
            <View style={styles.row}>
              <Text>Habilidades Blandas: {surveySummary.general?.completionRates?.softSkills || 0}%</Text>
            </View>
            <View style={styles.row}>
              <Text>Inteligencias Múltiples: {surveySummary.general?.completionRates?.hardSkills || 0}%</Text>
            </View>
            <View style={styles.row}>
              <Text>Detección de Adicciones: {surveySummary.general?.completionRates?.addictionDetection || 0}%</Text>
            </View>
            <View style={styles.row}>
              <Text>Bullying: {surveySummary.general?.completionRates?.bullying || 0}%</Text>
            </View>
            <View style={styles.row}>
              <Text>Entrevistas: {surveySummary.general?.completionRates?.interview || 0}%</Text>
            </View>
          </View>
        )}

        {/* Resumen de estadísticas */}
        <View style={styles.summaryGrid}>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryTitle}>Adicciones</Text>
            <Text style={styles.summaryNumber}>
              {riskStats?.addiction?.total || 0}
            </Text>
            <Text style={styles.summaryLabel}>Evaluaciones</Text>
          </View>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryTitle}>Bullying</Text>
            <Text style={styles.summaryNumber}>
              {riskStats?.bullying?.total || 0}
            </Text>
            <Text style={styles.summaryLabel}>Evaluaciones</Text>
          </View>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryTitle}>Alto Riesgo</Text>
            <Text style={styles.summaryNumber}>
              {riskStats?.totalHighRiskUsers || 0}
            </Text>
            <Text style={styles.summaryLabel}>Usuarios</Text>
          </View>
        </View>

        {/* Estadísticas de Adicciones */}
        {riskStats?.addiction && (
          <View style={styles.statCard}>
            <Text style={styles.statTitle}>Detección de Adicciones</Text>
            <View style={styles.riskLevel}>
              <Text style={styles.riskLabel}>Bajo Riesgo</Text>
              <Text style={styles.riskPercentage}>
                {riskStats.addiction.percentages?.lowRisk || 0}% ({riskStats.addiction.lowRisk || 0})
              </Text>
            </View>
            <View style={styles.riskLevel}>
              <Text style={styles.riskLabel}>Riesgo Moderado</Text>
              <Text style={styles.riskPercentage}>
                {riskStats.addiction.percentages?.moderateRisk || 0}% ({riskStats.addiction.moderateRisk || 0})
              </Text>
            </View>
            <View style={styles.riskLevel}>
              <Text style={styles.riskLabel}>Alto Riesgo</Text>
              <Text style={styles.riskPercentage}>
                {riskStats.addiction.percentages?.highRisk || 0}% ({riskStats.addiction.highRisk || 0})
              </Text>
            </View>
          </View>
        )}

        {/* Estadísticas de Bullying */}
        {riskStats?.bullying && (
          <View style={styles.statCard}>
            <Text style={styles.statTitle}>Bullying y Violencia</Text>
            <View style={styles.riskLevel}>
              <Text style={styles.riskLabel}>Bajo Riesgo</Text>
              <Text style={styles.riskPercentage}>
                {riskStats.bullying.percentages?.lowRisk || 0}% ({riskStats.bullying.lowRisk || 0})
              </Text>
            </View>
            <View style={styles.riskLevel}>
              <Text style={styles.riskLabel}>Riesgo Moderado</Text>
              <Text style={styles.riskPercentage}>
                {riskStats.bullying.percentages?.moderateRisk || 0}% ({riskStats.bullying.moderateRisk || 0})
              </Text>
            </View>
            <View style={styles.riskLevel}>
              <Text style={styles.riskLabel}>Alto Riesgo</Text>
              <Text style={styles.riskPercentage}>
                {riskStats.bullying.percentages?.highRisk || 0}% ({riskStats.bullying.highRisk || 0})
              </Text>
            </View>
            <View style={styles.riskLevel}>
              <Text style={styles.riskLabel}>Riesgo Grave</Text>
              <Text style={styles.riskPercentage}>
                {riskStats.bullying.percentages?.severeRisk || 0}% ({riskStats.bullying.severeRisk || 0})
              </Text>
            </View>
          </View>
        )}

        {/* Análisis de Inteligencias Múltiples */}
        {surveySummary?.hardSkillsAnalysis && (
          <View style={styles.card}>
            <Text style={styles.subtitle}>Análisis de Inteligencias Múltiples</Text>
            <View style={styles.row}>
              <Text>Total completadas: {surveySummary.hardSkillsAnalysis.totalCompleted || 0}</Text>
            </View>
            {surveySummary.hardSkillsAnalysis.topIntelligence && (
              <View style={styles.row}>
                <Text>Inteligencia más común: {surveySummary.hardSkillsAnalysis.topIntelligence}</Text>
              </View>
            )}
            {surveySummary.hardSkillsAnalysis.intelligenceDistribution && (
              <View>
                <Text style={styles.statTitle}>Distribución por Inteligencia:</Text>
                {Object.entries(surveySummary.hardSkillsAnalysis.intelligenceDistribution).map(([intelligence, count]) => (
                  <View key={intelligence} style={styles.riskLevel}>
                    <Text style={styles.riskLabel}>{intelligence}</Text>
                    <Text style={styles.riskPercentage}>{count} usuarios</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        )}

        {/* 🆕 Gráficas de Análisis */}
        <Text style={styles.subtitle}>Análisis Visual de Datos</Text>
        
        {/* Gráfica Simple de Prueba */}
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Porcentaje de Completado por Encuesta</Text>
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Demográficos: 45 (100%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ████████████████████░░ 100%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Habilidades Blandas: 38 (84%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ████████████████░░░░░░ 84%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Inteligencias Múltiples: 40 (89%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ██████████████████░░░░ 89%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Detección de Adicciones: 25 (56%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ████████████░░░░░░░░░░ 56%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Bullying: 20 (44%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ██████████░░░░░░░░░░░░ 44%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Entrevistas: 15 (33%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ████████░░░░░░░░░░░░░░ 33%
          </Text>
        </View>

        {/* Gráfica de Completado Real */}
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Porcentaje de Completado por Encuesta (Datos Reales)</Text>
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Demográficos: 12 (16%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ████░░░░░░░░░░░░░░░░░░ 16%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Habilidades Blandas: 67 (87%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ██████████████████░░░░ 87%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Inteligencias Múltiples: 77 (100%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ████████████████████░░ 100%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Detección de Adicciones: 77 (100%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ████████████████████░░ 100%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Bullying: 73 (95%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ██████████████████░░░░ 95%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Entrevistas: 65 (84%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ██████████████████░░░░ 84%
          </Text>
        </View>

        {/* Gráfica de Inteligencias Múltiples */}
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Distribución de Inteligencias Múltiples</Text>
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Inteligencia Comunicativa: 12 (80%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ████████████████░░░░░░ 80%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Inteligencia Matemática: 8 (53%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ████████████░░░░░░░░░░ 53%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Inteligencia Visual: 15 (100%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ████████████████████░░ 100%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Inteligencia Motriz: 10 (67%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ██████████████░░░░░░░░ 67%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Inteligencia Rítmica: 6 (40%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ████████░░░░░░░░░░░░░░ 40%
          </Text>
        </View>

        {/* Gráfica de Nivel Académico */}
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Distribución por Nivel Académico</Text>
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Secundaria: 25 (56%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ██████████████░░░░░░░░ 56%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Media Superior: 30 (67%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ████████████████░░░░░░ 67%
          </Text>
          
          <Text style={{ fontSize: 10, color: '#2c3e50', marginBottom: 3 }}>
            Superior: 45 (100%)
          </Text>
          <Text style={{ fontSize: 8, color: '#6c757d', fontFamily: 'Courier', marginBottom: 5 }}>
            ████████████████████░░ 100%
          </Text>
        </View>

        {/* Usuarios de Alto Riesgo */}
        {riskStats?.highRiskUsers && riskStats.highRiskUsers.length > 0 && (
          <View>
            <Text style={styles.subtitle}>Usuarios de Alto Riesgo</Text>
            {riskStats.highRiskUsers.map((user, index) => (
              <View key={user.id || index} style={styles.userCard}>
                <Text style={styles.userName}>{user.name}</Text>
                <Text style={styles.userInfo}>Email: {user.email}</Text>
                <Text style={styles.userInfo}>Nivel Académico: {user.academic_level}</Text>
                {user.addictionRisk && user.addictionRisk !== 'No evaluado' && (
                  <Text style={styles.riskIndicator}>
                    Adicciones: {user.addictionRisk}
                  </Text>
                )}
                {user.bullyingRisk && user.bullyingRisk !== 'No evaluado' && (
                  <Text style={styles.riskIndicator}>
                    Bullying: {user.bullyingRisk}
                  </Text>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Footer */}
        <Text style={styles.footer}>
          {PLATFORM_PDF_FOOTER}
        </Text>
      </Page>
    </Document>
  );
};

export default AdminReportPDF;
